import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  Checkbox,
  TextInput,
  Button,
  BadgeGroup
} from '@burna/monster-design-system';
import { APIName } from 'config';
import SettingsTable from 'components/settings-table';
import ScanResults from 'components/scan-results';
import useUser from 'hooks/useUser';
import useScanner from 'hooks/useScanner';
import styles from './index.module.scss';
import RowItemSettingList from 'components/row-item-setting-list';
import RowItemScanList from 'components/row-item-scan-list';
import { usePageWidthLessThan } from 'hooks/usePageWidthLessThan';

function ManageSettings({ initialFileUrlList }) {
  const [isTabletSize] = usePageWidthLessThan();
  const location = useLocation();
  const user = useUser();
  const scanner = useScanner({ managePath: location.pathname });
  const [scanClicked, setScanClicked] = useState(false);
  const [urlGenerated, setUrlGenerated] = useState(false);
  const [{ loading, error, message }, setStatus] = useState({});
  const [urlList, setUrlList] = useState(initialFileUrlList);
  const [settings, setSettings] = useState({
    oneTimeDownload: false,
    password: '',
    expiration: ''
  });
  useEffect(() => setUrlList(initialFileUrlList), [initialFileUrlList]);

  const totalScanners = scanner.data?.length;
  const finishedScans = useMemo(
    () => scanner.data?.filter(item => item.progress === 'scanned').length,
    [scanner.data]
  );

  const generateNewUrl = () => {
    setStatus({ loading: true, message: 'Generating new URL... Please wait!' });
    axios
      .put(`/api/${APIName}/settings`, {
        ...settings,
        expiration:
          settings.expiration && new Date(settings.expiration).getTime(),
        managePath: location.pathname.replace('/manage/', '')
      })
      .then(({ data }) => {
        setUrlGenerated(true);
        setStatus({
          loading: false,
          error: false,
          message: 'New URL generated successfully!'
        });
        if (data?.url) {
          setUrlList(prevArray => {
            return [
              {
                url: data.url,
                ...settings
              },
              ...prevArray
            ];
          });
        }
      })
      .catch(() => {
        setStatus({
          loading: false,
          error: true,
          message: 'Failed to generate new URL. Try again!'
        });
      });
  };

  const scanFile = () => {
    setScanClicked(true);
    setStatus({ loading: true, message: 'Initiating scan... Please wait!' });
    scanner
      .scan()
      .then(() => {
        setStatus({
          loading: false,
          error: false,
          message: 'Scan initiated successfully!'
        });
      })
      .catch(() => {
        setStatus({
          loading: false,
          error: true,
          message: 'Failed to initiate scan. Try again!'
        });
      });
  };

  const handleSettingsChange = valueObject => {
    setSettings(prevSettings => ({
      ...prevSettings,
      ...valueObject
    }));
  };

  const hasChanged = Object.values(settings).some(setting => !!setting);
  const shouldShowScanError = scanClicked && scanner.error;
  const shouldShowScanning = scanClicked && !scanner.error && !scanner.data;

  return (
    <>
      <Checkbox
        className={styles.checkbox}
        text="One-Time Download"
        checked={settings.oneTimeDownload}
        onChange={checked => handleSettingsChange({ oneTimeDownload: checked })}
      />
      <div className={styles.inputs}>
        <TextInput
          className={styles.input}
          title="Password"
          type="password"
          placeholder="Enter Password"
          icon="lock"
          value={settings.password}
          onChange={e => handleSettingsChange({ password: e.target.value })}
        />
        <TextInput
          className={styles.inputDate}
          title="Expiration Time"
          type="datetime-local"
          placeholder="mm / dd / yyyy , -- : --"
          icon="calendar"
          value={settings.expiration}
          onChange={e => handleSettingsChange({ expiration: e.target.value })}
        />
      </div>
      <div className={styles.submitContainer}>
        <Button
          className={styles.button}
          hierarchy="secondary-outlined"
          onClick={generateNewUrl}
          disabled={!hasChanged || loading}
          text="Get New URL"
        />
        <Button
          className={styles.button}
          onClick={scanFile}
          disabled={(scanClicked && !scanner.error) || loading}
          text="Scan Files"
        />
        {shouldShowScanning && (
          <BadgeGroup
            type="warning"
            badgeLabel="Please Wait..."
            label="Scanning process initiated"
            className={styles.message}
          />
        )}
        {shouldShowScanError && (
          <BadgeGroup
            type="error"
            badgeLabel="Error"
            label="There was an error while trying to scan the file"
            className={styles.message}
          />
        )}
        {urlGenerated && !shouldShowScanning && !shouldShowScanError && (
          <BadgeGroup
            type="success"
            badgeLabel="Successful"
            label="New URL generated successfully!"
            className={styles.message}
          />
        )}
      </div>

      {isTabletSize ? (
        urlList?.length > 0 ? (
          <div>
            <h2 className={styles.title}>All file URLs with their Settings</h2>
            <RowItemSettingList data={urlList} />
          </div>
        ) : null
      ) : (
        <SettingsTable urlList={urlList} />
      )}

      {isTabletSize ? (
        scanner.data?.length > 0 ? (
          <>
            <h2 className={styles.title}>Scan Results</h2>
            <RowItemScanList
              data={scanner.data}
              outOfTime={scanner.outOfTime}
            />
          </>
        ) : null
      ) : (
        <ScanResults
          data={scanner.data}
          totalScanners={totalScanners}
          finishedScans={finishedScans}
          outOfTime={scanner.outOfTime}
        />
      )}
    </>
  );
}

export default ManageSettings;
